// Where the chosen interface language lives between visits — caiet ACCS-03.
//
// It lives in the browser, per device, and deliberately not on the server.
// SIMSM keeps no user profile: MPass owns identity and the caiet gives this
// system no user management at all (UC10), so there is no per-user row to hang
// a preference on and inventing one would be inventing a user store. A browser
// preference also covers the routes a server preference could not — the
// landing page, the MPass login and the role selector are anonymous, and the
// language has to survive there too.
//
// Tolgee ships `LanguageStorage()` and it is deliberately not used. Its
// localStorage calls run through the core's `handleRegularOrAsyncErr`, which
// re-throws after reporting; a browser configured to block site data (or
// Safari's private mode) therefore turns `tolgee.run()` into a throw and the
// whole application fails to boot. The middleware below is the same extension
// point with the failure mode a public service needs: unreadable storage means
// "no preference stated", never a page that will not load.

import type { LanguageStorageMiddleware, TolgeeInstance, TolgeePlugin } from '@tolgee/web';
import type { Locale } from '@simsm/shared-types';
import { SUPPORTED_LOCALES } from './locales';

/** `simsm.`-namespaced like the other keys this origin owns, so no vendor key can collide with it. */
export const LOCALE_STORAGE_KEY = 'simsm.locale';

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** The stored preference, or `undefined` when there is none, it is unreadable, or it is not a language SIMSM speaks. */
export function readStoredLocale(): Locale | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function writeStoredLocale(locale: string): void {
  if (!isSupportedLocale(locale)) return;
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage refused. The choice still holds for this page; it just cannot
    // outlive it, which is the most this browser allows.
  }
}

export function createLocaleStorage(): LanguageStorageMiddleware {
  return {
    getLanguage: () => readStoredLocale(),
    setLanguage: (language: string) => writeStoredLocale(language),
  };
}

/**
 * Reads the preference when Tolgee resolves its initial language and writes it
 * back on every `changeLanguage`. Requires `defaultLanguage` rather than
 * `language` in the init options: `language` pins the state up front and the
 * core then skips the storage entirely.
 */
export const LocalePreference =
  (): TolgeePlugin =>
  (tolgee: TolgeeInstance, tools): TolgeeInstance => {
    tools.setLanguageStorage(createLocaleStorage());
    return tolgee;
  };
